/**
 * QMS Drag and Drop Hook
 * Manages drag/drop state and handlers for the Kanban board
 */

import { useState } from 'react';
import {
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { toast } from 'sonner';
import { useMoveQmsTicket } from './useQmsBoard';
import type { QmsTicket, QmsColumn, QmsTicketStatus } from '../types/qms.types';
import { isQmsMovableStatus } from '../constants/qmsBoard';

interface UseQmsDragDropOptions {
  columns: QmsColumn[];
  filteredBoard: Record<string, QmsTicket[]>;
  /**
   * Organisation id the board is currently scoped to. Sent in the move payload
   * because the V2 session does not always carry `user.organisation`, so the
   * backend cannot derive it from the request headers alone.
   */
  organisation?: string;
  /**
   * Optional callback to intercept moves before they happen.
   * If provided, it's called with the ticket, new status, and an onProceed callback.
   * Call onProceed() when ready to complete the move.
   * If not provided, moves happen immediately (backward compatible).
   */
  onBeforeMove?: (params: {
    ticket: QmsTicket;
    newStatus: QmsTicketStatus;
    newPosition: number;
    onProceed: () => void;
  }) => void;
}

interface UseQmsDragDropResult {
  // Sensors for dnd-kit
  sensors: ReturnType<typeof useSensors>;

  // Active ticket being dragged
  activeTicket: QmsTicket | null;

  // Mutation state
  isMoving: boolean;

  // Event handlers
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
}

/**
 * Hook for managing drag/drop functionality on the QMS Kanban board
 */
export function useQmsDragDrop({
  columns,
  filteredBoard,
  organisation,
  onBeforeMove,
}: UseQmsDragDropOptions): UseQmsDragDropResult {
  const moveTicketMutation = useMoveQmsTicket();
  const [activeTicket, setActiveTicket] = useState<QmsTicket | null>(null);

  // Configure drag sensors - both pointer and keyboard
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const ticketId = event.active.id as string;

    // Find the ticket across all columns
    let foundTicket: QmsTicket | null = null;
    Object.values(filteredBoard).forEach((tickets) => {
      const ticket = tickets.find((t) => t._id === ticketId);
      if (ticket) {
        foundTicket = ticket;
      }
    });

    const ticket = foundTicket as QmsTicket | null;
    setActiveTicket(ticket);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveTicket(null);

    if (!over) return;

    const ticketId = active.id as string;
    const overId = over.id as string;

    // Find the target column definition
    // First, check if overId is a column key directly
    let targetColumn = columns.find((c) => c.key === overId);
    let targetColumnKey = overId;

    // If not a column key, overId might be a ticket ID (when dropping on another ticket)
    // Find which column contains that ticket
    if (!targetColumn) {
      for (const columnKey of Object.keys(filteredBoard)) {
        const tickets = filteredBoard[columnKey];
        if (tickets?.some((t) => t._id === overId)) {
          targetColumn = columns.find((c) => c.key === columnKey);
          targetColumnKey = columnKey;
          break;
        }
      }
    }

    // If still no target column found, exit
    if (!targetColumn) return;

    // Find the source ticket to validate type compatibility
    let sourceTicket: QmsTicket | undefined;
    let sourceColumnKey: string | undefined;
    for (const columnKey of Object.keys(filteredBoard)) {
      const tickets = filteredBoard[columnKey];
      if (tickets) {
        const found = tickets.find((t) => t._id === ticketId);
        if (found) {
          sourceTicket = found;
          sourceColumnKey = columnKey;
          break;
        }
      }
    }

    // Don't do anything if dropping on the same column
    if (sourceColumnKey === targetColumnKey) return;

    // Validate: ticket type must match column type
    if (sourceTicket && sourceTicket.type !== targetColumn.type) {
      console.warn('Cannot move ticket to column of different type');
      toast.error('Cannot move ticket to a column of different type');
      return;
    }

    // Validate: status must be in allowed list (matches backend validation)
    if (!isQmsMovableStatus(targetColumn.status)) {
      console.warn('Invalid target status:', targetColumn.status);
      toast.error(`Invalid status: ${targetColumn.status}`);
      return;
    }

    // Get the current tickets in the target column for position calculation
    const targetColumnTickets = filteredBoard[targetColumnKey] || [];
    const newStatus = targetColumn.status as QmsTicketStatus;
    const newPosition = targetColumnTickets.length + 1;

    // Function to perform the actual move
    const performMove = () => {
      // Only forward a concrete org id — the 'all' scope (full-admin client
      // view) targets multiple orgs and must not be sent as the ticket's org.
      const payloadOrganisation = organisation && organisation !== 'all'
        ? organisation
        : undefined;
      moveTicketMutation.mutate({
        ticketId,
        payload: {
          status: newStatus,
          position: newPosition,
          ...(payloadOrganisation ? { organisation: payloadOrganisation } : {}),
        },
      });
    };

    // If onBeforeMove is provided, let it decide when to proceed
    if (onBeforeMove && sourceTicket) {
      onBeforeMove({
        ticket: sourceTicket,
        newStatus,
        newPosition,
        onProceed: performMove,
      });
    } else {
      // No interception - proceed immediately
      performMove();
    }
  };

  return {
    sensors,
    activeTicket,
    isMoving: moveTicketMutation.isPending,
    handleDragStart,
    handleDragEnd,
  };
}
